import { Badge, Group, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { formatPhone } from '@/utils'

const LABEL = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  unknown: 'Status unavailable',
} as const

const COLOR = {
  queued: 'gray',
  sent: 'blue',
  delivered: 'green',
  failed: 'red',
  unknown: 'gray',
} as const

/** Delivery status of the fax that sent this order (PD-1651). Nothing for orders never faxed. */
export const OrderFaxStatus = ({ requestGroupId }: { requestGroupId: string }) => {
  const orpc = useOrpc()
  const { data } = useQuery({
    ...orpc.orderFaxStatus.queryOptions({ input: { requestGroupId } }),
    staleTime: 30_000,
    retry: 1,
    // A fax still on its way can still fail: keep checking until RingCentral settles it.
    refetchInterval: (query) =>
      query.state.data?.status === 'queued' || query.state.data?.status === 'sent' ? 30_000 : false,
  })
  if (!data || data.status === 'none') return null
  return (
    <Group gap="xs" mb="sm" wrap="wrap">
      <Text size="sm" fw={500}>
        Fax delivery
      </Text>
      <Badge color={COLOR[data.status]} variant="light">
        {LABEL[data.status]}
      </Badge>
      {data.recipient && (
        <Text size="xs" c="dimmed">
          to {formatPhone(data.recipient)}
        </Text>
      )}
      {data.sentAt && (
        <Text size="xs" c="dimmed">
          ·{' '}
          {new Date(data.sentAt).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </Text>
      )}
      {data.status === 'failed' && (
        <Text size="xs" c="red">
          The fax did not go through. Check the number and send a new referral.
        </Text>
      )}
    </Group>
  )
}
